import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { GCInstant as platform } from 'src/lib/gcinstant';
import {
  shopBundlesGetGemsReward,
  shopBundlesIsPack,
  shopBundlesHasPurchasedStarterPack,
} from 'src/replicant/match2/shopbundles/match2-shopbundles.getters';
import StateObserver, { now } from 'src/StateObserver';
import {
  shopBundlesAvailableProducts,
  shopBundlesPacks,
} from 'src/replicant/match2/shopbundles/match2-shopbundles.ruleset';
import { DeepLink } from 'src/game/launch/deepLinks';
import type { CrossplayTracking } from 'src/game/crossplay/crossplay.types';
import { canShowStarterPackPromo } from 'src/game/starterpack/starterpack.utils';
import { getStarterPackProductId } from 'src/replicant/starterpack/starterpack.getters';
import { track } from 'src/lib/analytics/utils';

/**
 * Open the match2 shop bundles popup, optionally with gemsNeeded parameter
 * to show from start only a couple of products that give enough gems - user
 * can still click on "more" button to see the remaining products that give
 * enough gems. Products granting less than gemsNeeded are never offered,
 * since buying one would leave the player short of the amount they need.
 *
 * @returns 'success' if user made a purchase, 'close' if user closed the popup
 */
export async function match2ShopBundlesOpenShop(args: {
  gemsNeeded?: number;
  origin: PopupDialogOrigin | undefined;
  additionalTracking?: Record<string, string>;
  additionalContext?: DeepLink[];
  crossplayTracking: CrossplayTracking;
}): Promise<'success' | 'close'> {
  const user = StateObserver.getState().user;
  const availableProducts = shopBundlesAvailableProducts.filter(
    // Only list products without the 'hidden' or 'promo' tag
    (p) =>
      shopBundlesPacks[p]?.tag !== 'hidden' &&
      shopBundlesPacks[p]?.tag !== 'starterPackPromo',
  );

  // Add promo products to available list, if applicable
  if (canShowStarterPackPromo(user, now())) {
    const starterPackProduct = getStarterPackProductId(user);
    if (starterPackProduct) {
      availableProducts.unshift(starterPackProduct);
    }
  }

  const gemsNeeded = args?.gemsNeeded || 0;

  // Remove starter pack if it has already been purchased
  if (shopBundlesHasPurchasedStarterPack(user)) {
    const index = availableProducts.indexOf('buruburu_starter_pack');
    availableProducts.splice(index, 1);
  }

  const orderedProducts = availableProducts;

  if (gemsNeeded <= 0) {
    track('ShopOpen', {
      isCatalogLoaded: platform.catalog.length > 0,
      type: 'gemShop',
      firstVisiblePack: orderedProducts[0],
    });

    // Show all products available if gemsNeeded is not specified
    return await openPopupPromise('popupMatch2ShopBundles', {
      products: orderedProducts,
      additionalContext: args.additionalContext,
      crossplayTracking: args.crossplayTracking,
      origin: args.origin,
    });
  }

  // Narrow the catalog once so that every list handed to the popup, including
  // the "more" one, can only contain products that cover gemsNeeded
  const sufficientProducts = orderedProducts.filter(
    (p) => shopBundlesGetGemsReward(p) >= gemsNeeded,
  );

  // Find pack products, sorted by total gems reward, ascending
  const packs = sufficientProducts.filter((p) => shopBundlesIsPack(p));

  packs.sort(
    (a, b) => shopBundlesGetGemsReward(a) - shopBundlesGetGemsReward(b),
  );

  // Pick the 2 cheapest products that give enough gems
  const selectedPacks = packs.slice(0, 2);

  // Find gems products, sorted by total gems reward, ascending
  const gems = sufficientProducts
    .filter((p) => !shopBundlesIsPack(p))
    .sort((a, b) => shopBundlesGetGemsReward(a) - shopBundlesGetGemsReward(b));

  // Pick the 2 cheapest products that give enough gems
  const selectedGems = gems.slice(0, 2);

  // Combine selected products into the main products list
  const products = [...selectedPacks, ...selectedGems];

  track('ShopOpen', {
    isCatalogLoaded: platform.catalog.length > 0,
    type: 'pinchShop',
    firstVisiblePack: products[0],
  });

  // Show the shop popup with selected products
  return await openPopupPromise('popupMatch2ShopBundles', {
    products,
    // The popup only renders the "more" button when allProducts is set
    allProducts:
      sufficientProducts.length > products.length
        ? sufficientProducts
        : undefined,
    additionalTracking: args?.additionalTracking,
    additionalContext: args?.additionalContext,
    crossplayTracking: args.crossplayTracking,
    origin: args.origin,
  });
}
